import { theme } from 'lib/theme';
import React from 'react';
import {
  Animated,
  Easing,
  LayoutChangeEvent,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { Icon, ICONS } from './Icon';
import { Pressable } from './Pressable';
import { Txt } from './Txt';

const DEFAULT_ANIMATION_DURATION = 180;

type CollapsibleProps = {
  children: React.ReactNode;
  expanded: boolean;
  duration?: number;
  onCollapseEnd?: () => void;
  style?: StyleProp<ViewStyle>;
  unmountOnCollapse?: boolean;
};

export const Collapsible: React.FC<CollapsibleProps> = ({
  children,
  expanded,
  duration = DEFAULT_ANIMATION_DURATION,
  onCollapseEnd,
  style,
  unmountOnCollapse = true,
}) => {
  const content = React.Children.toArray(children).filter(Boolean);
  const hasContent = content.length > 0;
  const progress = React.useRef(new Animated.Value(expanded ? 1 : 0)).current;
  const previousExpanded = React.useRef(expanded);
  const previousHasContent = React.useRef(hasContent);
  const opening = React.useRef(false);
  const [contentHeight, setContentHeight] = React.useState(0);
  const [rendered, setRendered] = React.useState(expanded && hasContent);

  const animateOpen = React.useCallback(() => {
    opening.current = false;
    Animated.timing(progress, {
      duration,
      easing: Easing.inOut(Easing.ease),
      toValue: 1,
      useNativeDriver: false,
    }).start();
  }, [duration, progress]);

  React.useEffect(() => {
    const expandedChanged = previousExpanded.current !== expanded;
    const contentBecameAvailable = !previousHasContent.current && hasContent;
    previousExpanded.current = expanded;
    previousHasContent.current = hasContent;

    if (!hasContent) {
      progress.stopAnimation();
      opening.current = false;
      setRendered(false);
      if (!expanded) {
        onCollapseEnd?.();
      }
      return;
    }

    if (contentBecameAvailable && expanded && !expandedChanged) {
      progress.setValue(1);
      setRendered(true);
      return;
    }

    if (!expandedChanged) {
      return;
    }
    progress.stopAnimation();

    if (expanded) {
      progress.setValue(0);
      setRendered(true);
      if (contentHeight) {
        animateOpen();
      } else {
        opening.current = true;
      }
      return;
    }

    opening.current = false;
    Animated.timing(progress, {
      duration,
      easing: Easing.inOut(Easing.ease),
      toValue: 0,
      useNativeDriver: false,
    }).start(({ finished }) => {
      if (finished) {
        if (unmountOnCollapse) {
          setRendered(false);
        }
        onCollapseEnd?.();
      }
    });
  }, [
    animateOpen,
    contentHeight,
    duration,
    expanded,
    hasContent,
    onCollapseEnd,
    progress,
    unmountOnCollapse,
  ]);

  const handleLayout = React.useCallback(
    (event: LayoutChangeEvent) => {
      const nextContentHeight = event.nativeEvent.layout.height;
      setContentHeight(nextContentHeight);
      if (opening.current && nextContentHeight) {
        animateOpen();
      }
    },
    [animateOpen],
  );

  if (!rendered || !hasContent) {
    return null;
  }

  return (
    <Animated.View
      accessibilityElementsHidden={!expanded}
      importantForAccessibility={expanded ? 'auto' : 'no-hide-descendants'}
      pointerEvents={expanded ? 'auto' : 'none'}
      style={[
        style,
        {
          height: progress.interpolate({
            inputRange: [0, 1],
            outputRange: [0, contentHeight],
          }),
          opacity: progress,
          overflow: 'hidden',
        },
      ]}
    >
      <View onLayout={handleLayout} style={styles.content}>
        {content}
      </View>
    </Animated.View>
  );
};

export const CollapsibleSection: React.FC<{
  children: React.ReactNode;
  expanded: boolean;
  label: string;
  onToggle: () => void;
  unmountOnCollapse?: boolean;
}> = ({ children, expanded, label, onToggle, unmountOnCollapse }) => {
  const [collapseComplete, setCollapseComplete] = React.useState(!expanded);

  React.useEffect(() => {
    if (expanded) {
      setCollapseComplete(false);
    }
  }, [expanded]);

  const handleCollapseEnd = React.useCallback(() => {
    setCollapseComplete(true);
  }, []);

  return (
    <View>
      <Pressable
        style={[
          styles.toggleLabel,
          !collapseComplete && styles.toggleLabelExpanded,
        ]}
        pressedStyle={styles.toggleLabelPressed}
        onPress={onToggle}
        accessibilityLabel={label}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
      >
        <Icon
          source={ICONS.ChevronRightSolid}
          color={theme.colors.text}
          size={12}
          style={expanded ? styles.expandedChevron : undefined}
        />
        <Txt size="small" weight="medium">
          {label}
        </Txt>
      </Pressable>
      <Collapsible
        expanded={expanded}
        onCollapseEnd={handleCollapseEnd}
        unmountOnCollapse={unmountOnCollapse}
      >
        {children}
      </Collapsible>
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  expandedChevron: {
    transform: [{ rotate: '90deg' }],
  },
  toggleLabel: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.padding / 2,
    marginHorizontal: theme.padding * 2,
  },
  toggleLabelExpanded: {
    marginBottom: theme.padding,
  },
  toggleLabelPressed: {
    opacity: 0.64,
  },
});
